import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { addLocale } from "primereact/api";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import type { DataTableStateEvent } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { OverlayPanel } from "primereact/overlaypanel";
import type { PaginatorTemplateOptions } from "primereact/paginator";
import { Toast } from "primereact/toast";

import { AUDIT_TABLE_DENSITY_KEY } from "@/core/constants/keys";
import { Paths } from "@/core/constants/paths";
import {
  AUDIT_ACTION_OPTIONS,
  AUDIT_MODULE_OPTIONS,
  DEFAULT_AUDIT_SORT,
  EMPTY_AUDIT_FILTERS,
  formatAuditModule,
  isAuditSortField,
  listAllAuditRecords,
  useAuditRecords,
  useAuditResponsavelOptions,
} from "@/modules/audit";
import type {
  AuditAction,
  AuditFilterOption,
  AuditFilters,
  AuditModule,
  AuditRecord,
  AuditResult,
  AuditSort,
  AuditSortField,
} from "@/modules/audit";
import { clearAuthToken } from "@/modules/auth";
import { useTableExport } from "@/modules/export";
import { ACTION_TOOLTIP_OPTIONS, DETAIL_ACTION_TOOLTIP } from "../components/action-tooltip";
import { EmptyState, resolveEmptyStateKind } from "../components/empty-state";
import type { EmptyStateContent } from "../components/empty-state";
import { createExportFeedback, ExportTableDialog } from "../components/export-table-dialog";
import { StateMessage } from "../components/state-message";
import { AuditDetailDialog } from "./components/audit-detail-dialog";
import { ResponsavelTooltip } from "./components/responsavel-tooltip";
import {
  ALL_AUDIT_COLUMNS,
  AUDIT_COLUMN_OPTIONS,
  TableSettingsDialog,
} from "./components/table-settings-dialog";
import type {
  AuditColumnKey,
  AuditDensity,
  AuditTableSettings,
} from "./components/table-settings-dialog";
import { formatAuditDateTime } from "./audit-date-time";
import styles from "./audit-screen.module.scss";

/** Painel sem registros: a listagem está vazia porque ainda não há o que exibir. */
const EMPTY_STATE: EmptyStateContent = {
  title: "Nenhum registro de auditoria",
  description: "Assim que houver ações registradas no Hub, elas aparecerão nesta listagem.",
};

/** Filtros aplicados sem registros — `ilu-no-filters` do Figma. */
const EMPTY_FILTER_STATE: EmptyStateContent = {
  title: "Nenhum registro corresponde aos filtros selecionados.",
  description: "Tente ajustar ou limpar os filtros",
};

const DEFAULT_ROWS_PER_PAGE = 10;

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const ROWS_PER_PAGE_DROPDOWN_OPTIONS = ROWS_PER_PAGE_OPTIONS.map((rows) => ({
  label: `${rows} por página`,
  value: rows,
}));

const CURRENT_PAGE_REPORT = "Mostrando {first} a {last} de {totalRecords} registros";

/**
 * Sem seleção o filtro não restringe a consulta e o campo anuncia o critério que
 * representa — mesmo padrão dos selects da tela de Usuários, onde escolher a
 * opção com o nome do campo é o que devolve o filtro ao estado vazio.
 */
const ANY_ALTERACAO: AuditFilterOption = { label: "Alteração", value: "" };
const ANY_MODULO: AuditFilterOption = { label: "Módulo", value: "" };
const ANY_RESPONSAVEL: AuditFilterOption = { label: "Responsável", value: "" };

const ALTERACAO_FILTER_PLACEHOLDER = "Buscar alteração";
const MODULO_FILTER_PLACEHOLDER = "Buscar módulo";
const RESPONSAVEL_FILTER_PLACEHOLDER = "Buscar responsável";
const ALTERACAO_EMPTY_FILTER_MESSAGE = "Nenhuma alteração encontrada.";
const MODULO_EMPTY_FILTER_MESSAGE = "Nenhum módulo encontrado.";
const RESPONSAVEL_EMPTY_FILTER_MESSAGE = "Nenhum responsável encontrado.";

export const TABLE_SETTINGS_APPLIED_MESSAGE = "Configurações da tabela atualizada com sucesso.";

function isDensity(value: string | null): value is AuditDensity {
  return value === "confortavel" || value === "compacto";
}

/** A densidade escolhida acompanha o usuário entre visitas, como na tela de Usuários. */
function readStoredDensity(): AuditDensity {
  try {
    const stored = localStorage.getItem(AUDIT_TABLE_DENSITY_KEY);
    return isDensity(stored) ? stored : "confortavel";
  } catch {
    return "confortavel";
  }
}

function persistDensity(value: AuditDensity) {
  try {
    localStorage.setItem(AUDIT_TABLE_DENSITY_KEY, value);
  } catch {
    return;
  }
}

function readInitialSettings(): AuditTableSettings {
  return { density: readStoredDensity(), columns: ALL_AUDIT_COLUMNS };
}

/**
 * A coluna ordenada precisa estar em tela para exibir a direção aplicada (RN03).
 * Ação não entra em "Mostrar e Ocultar Colunas": identifica a linha e está
 * sempre visível.
 */
function isSortedColumnVisible(field: AuditSortField, columns: AuditColumnKey[]): boolean {
  return field === "acao" || columns.includes(field);
}

const EXPORT_FILE_NAME = "auditoria";
const EXPORT_TITLE = "Painel de Auditoria";

interface ExportColumn {
  label: string;
  read: (record: AuditRecord) => string;
}

function findColumnLabel(key: AuditColumnKey): string {
  return AUDIT_COLUMN_OPTIONS.find((option) => option.value === key)?.label ?? key;
}

/** Ação identifica a linha e está sempre em tela, por isso não é opcional. */
const EXPORT_ACTION_COLUMN: ExportColumn = { label: "Ação", read: (record) => record.acao };

const EXPORT_OPTIONAL_COLUMNS: Record<AuditColumnKey, ExportColumn> = {
  dataHora: {
    label: findColumnLabel("dataHora"),
    read: (record) => {
      const { date, time } = formatAuditDateTime(record.dataHora);

      return time ? `${date} ${time}` : date;
    },
  },
  valorAnteriorNovo: {
    label: findColumnLabel("valorAnteriorNovo"),
    read: (record) => [record.valorAnterior, record.valorNovo].filter(Boolean).join(" → "),
  },
  justificativa: {
    label: findColumnLabel("justificativa"),
    read: (record) => record.justificativa,
  },
  modulo: {
    label: findColumnLabel("modulo"),
    read: (record) => (record.modulo ? formatAuditModule(record.modulo) : ""),
  },
  responsavel: { label: findColumnLabel("responsavel"), read: (record) => record.responsavel },
};

const EXPORT_COLUMNS_AFTER_ACTION: AuditColumnKey[] = [
  "valorAnteriorNovo",
  "justificativa",
  "modulo",
  "responsavel",
];

/** AC2 — só as colunas visíveis, na ordem em que aparecem na listagem. */
function buildExportColumns(columns: AuditColumnKey[]): ExportColumn[] {
  const visible = new Set(columns);
  const ordered: ExportColumn[] = [];

  if (visible.has("dataHora")) ordered.push(EXPORT_OPTIONAL_COLUMNS.dataHora);
  ordered.push(EXPORT_ACTION_COLUMN);

  for (const key of EXPORT_COLUMNS_AFTER_ACTION) {
    if (visible.has(key)) ordered.push(EXPORT_OPTIONAL_COLUMNS[key]);
  }

  return ordered;
}

const COLUMN_WIDTHS: Record<string, CSSProperties> = {
  dataHora: { width: "9rem" },
  acao: { minWidth: "20rem" },
  justificativa: { minWidth: "13rem" },
  modulo: { minWidth: "8rem" },
  responsavel: { minWidth: "10rem" },
  acoes: { width: "3.5rem" },
};

// O projeto não possui camada de internacionalização: o calendário recebe o pt-BR aqui.
addLocale("pt-br", {
  firstDayOfWeek: 0,
  dayNames: ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"],
  dayNamesShort: ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"],
  dayNamesMin: ["D", "S", "T", "Q", "Q", "S", "S"],
  monthNames: [
    "janeiro",
    "fevereiro",
    "março",
    "abril",
    "maio",
    "junho",
    "julho",
    "agosto",
    "setembro",
    "outubro",
    "novembro",
    "dezembro",
  ],
  monthNamesShort: [
    "jan",
    "fev",
    "mar",
    "abr",
    "mai",
    "jun",
    "jul",
    "ago",
    "set",
    "out",
    "nov",
    "dez",
  ],
  today: "Hoje",
  clear: "Limpar",
  chooseDate: "Escolher o ano",
});

/** O calendário de Ano devolve 1º de janeiro do ano escolhido. */
function toYearDate(ano: number | null): Date | null {
  return ano === null ? null : new Date(ano, 0, 1);
}

function hasCriteria(filters: AuditFilters): boolean {
  return Boolean(filters.ano || filters.alteracao || filters.modulo || filters.responsavelId);
}

const PAGINATOR_TEMPLATE: PaginatorTemplateOptions = {
  layout:
    "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown",
  RowsPerPageDropdown: (options) => (
    <Dropdown
      // Mantém a classe do template padrão para o tema alcançar o seletor.
      className="p-paginator-rpp-options"
      panelClassName={styles.rowsPerPagePanel}
      value={options.value}
      options={ROWS_PER_PAGE_DROPDOWN_OPTIONS}
      onChange={options.onChange}
      aria-label="Registros por página"
    />
  ),
};

export function AuditScreen() {
  const navigate = useNavigate();
  const configPanel = useRef<OverlayPanel>(null);
  const toast = useRef<Toast>(null);

  const [filters, setFilters] = useState<AuditFilters>(EMPTY_AUDIT_FILTERS);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(DEFAULT_ROWS_PER_PAGE);
  // `null` é o terceiro estado do ciclo — sem ordenação escolhida, a listagem
  // segue na ordem padrão da tela e nenhum cabeçalho exibe a seta.
  const [sort, setSort] = useState<AuditSort | null>(null);
  const [settings, setSettings] = useState<AuditTableSettings>(readInitialSettings);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  // Registro em detalhamento; `null` mantém apenas a listagem em tela.
  const [detailId, setDetailId] = useState<string | null>(null);
  const [anoCalendarOpen, setAnoCalendarOpen] = useState(false);
  const { records, total, status, errorMessage, reload } = useAuditRecords({
    filters,
    page: Math.floor(first / rows) + 1,
    pageSize: rows,
    sort: sort ?? DEFAULT_AUDIT_SORT,
  });

  // Depois da listagem: a consulta da página pedida não espera as opções do select.
  const responsavelOptions = useAuditResponsavelOptions();

  /** AC2 — o arquivo leva o resultado completo da consulta, e não a página em tela. */
  const exportView = useTableExport({
    loadView: async () => {
      const columns = buildExportColumns(settings.columns);
      const applied = sort ?? DEFAULT_AUDIT_SORT;

      const allRecords = await listAllAuditRecords({
        status: filters.status,
        ano: filters.ano,
        alteracao: filters.alteracao,
        modulo: filters.modulo,
        responsavelId: filters.responsavelId,
        ordenarPor: applied.field,
        ordem: applied.direction,
      });

      return {
        fileName: EXPORT_FILE_NAME,
        title: EXPORT_TITLE,
        columns: columns.map((column) => column.label),
        rows: allRecords.map((record) => columns.map((column) => column.read(record))),
      };
    },
    ...createExportFeedback(toast),
  });

  const isLoading = status === "loading";
  const isFiltered = hasCriteria(filters);
  const emptyKind = resolveEmptyStateKind(false, isFiltered);
  const emptyContent = emptyKind === "filter" ? EMPTY_FILTER_STATE : EMPTY_STATE;

  // Só depois da consulta concluída: enquanto ela corre, a tabela segue em tela
  // com o indicador de carregamento e os registros da consulta anterior.
  const isEmpty = status === "success" && records.length === 0;
  const anoDate = useMemo(() => toYearDate(filters.ano), [filters.ano]);

  const visibleColumns = useMemo(() => new Set(settings.columns), [settings.columns]);
  const shows = (column: AuditColumnKey) => visibleColumns.has(column);

  // Um total menor (novo filtro, recarga) pode deixar a página atual fora da faixa.
  useEffect(() => {
    setFirst((current) => (current < total ? current : 0));
  }, [total]);

  /** RN003 — o novo critério entra em conjunto com os demais e a listagem volta à página 1. */
  function updateFilters(partial: Partial<AuditFilters>) {
    setFilters((current) => ({ ...current, ...partial }));
    setFirst(0);
  }

  /** RN004 — esvazia os cinco campos e reapresenta a listagem sem critérios. */
  function handleClear() {
    setFilters(EMPTY_AUDIT_FILTERS);
    setFirst(0);
  }

  /** RN005 — trocar de página ou de tamanho mantém os filtros aplicados. */
  function handlePage(event: { first: number; rows: number }) {
    setFirst(event.first);
    setRows(event.rows);
  }

  /**
   * Ordenação pelo cabeçalho no ciclo dos Padrões Globais de Interface, como na
   * tela de Usuários: crescente → decrescente → sem ordenação. O terceiro clique
   * devolve a listagem à ordem padrão da tela (Data/Hora, do mais recente para o
   * mais antigo). Os filtros, o tamanho de página e as colunas visíveis seguem em
   * vigor; a consulta é refeita a partir da página 1 (AC4).
   */
  function handleSort(event: DataTableStateEvent) {
    setSort(
      event.sortOrder && isAuditSortField(event.sortField)
        ? { field: event.sortField, direction: event.sortOrder === -1 ? "desc" : "asc" }
        : null,
    );
    setFirst(0);
  }

  /** RN001 — o menu ⋮ abre o modal Exportar Tabela. */
  function handleExportView() {
    configPanel.current?.hide();
    exportView.open();
  }

  function handleOpenSettings() {
    configPanel.current?.hide();
    setIsSettingsOpen(true);
  }

  /**
   * A escolha só entra em vigor em Aplicar; a consulta não é refeita. A exceção
   * é ocultar a coluna que ordena a listagem: sem o cabeçalho em tela o critério
   * ficaria sem indicação visual, então a ordenação volta ao padrão.
   */
  function handleApplySettings(applied: AuditTableSettings) {
    setSettings(applied);
    persistDensity(applied.density);
    setIsSettingsOpen(false);

    if (sort && !isSortedColumnVisible(sort.field, applied.columns)) {
      setSort(null);
      setFirst(0);
    }

    toast.current?.show({
      severity: "success",
      summary: "Sucesso",
      detail: TABLE_SETTINGS_APPLIED_MESSAGE,
      life: 3000,
      closable: true,
    });
  }

  /**
   * Abre o detalhamento sobre a listagem: sem troca de rota, os filtros, a
   * página, a ordenação e a densidade em vigor seguem intactos (AC4).
   */
  function handleDetail(record: AuditRecord) {
    setDetailId(record.id);
  }

  /** 401 no detalhamento: mesma reautenticação do interceptor do HttpClient. */
  function handleReauthenticate() {
    clearAuthToken();
    navigate(Paths.LOGIN, { replace: true });
  }

  /**
   * AC3 — só a coluna ordenada exibe a seta, na direção em vigor. O tema desenha
   * um ícone de duas pontas nos cabeçalhos ainda sem ordenação, que não faz parte
   * do padrão da tela. A direção é anunciada pelo `aria-sort` do cabeçalho, por
   * isso a seta em si fica fora da leitura assistiva.
   */
  function renderSortIcon({ sorted, sortOrder }: { sorted?: boolean; sortOrder?: number | null }) {
    if (!sorted || !sortOrder) return null;

    const ascending = sortOrder > 0;

    return (
      <i
        className={`pi ${ascending ? "pi-sort-amount-up-alt" : "pi-sort-amount-down"} ${styles.sortIcon}`}
        data-testid={`audit-sort-${ascending ? "asc" : "desc"}`}
        aria-hidden
      />
    );
  }

  function renderDataHora(record: AuditRecord) {
    const { date, time } = formatAuditDateTime(record.dataHora);

    return (
      <span className={styles.dateTime}>
        <span className={styles.dateTimeDate}>{date}</span>
        {time ? <span className={styles.dateTimeTime}>{time}</span> : null}
      </span>
    );
  }

  /**
   * A alteração aparece abaixo da ação: valor anterior riscado, seta e valor novo.
   * "Valor Anterior → Novo Valor" das Configurações da Tabela oculta apenas esse
   * par; a ação em si identifica a linha e continua sempre visível.
   */
  function renderAcao(record: AuditRecord) {
    const hasChange =
      shows("valorAnteriorNovo") && Boolean(record.valorAnterior || record.valorNovo);

    return (
      <div className={styles.actionCell}>
        <span className={styles.actionName}>{record.acao}</span>

        {hasChange ? (
          <span className={styles.actionChange} data-testid={`audit-change-${record.id}`}>
            {record.valorAnterior ? (
              <span className={styles.previousValue}>{record.valorAnterior}</span>
            ) : null}
            {record.valorAnterior && record.valorNovo ? (
              <i className={`pi pi-arrow-right ${styles.changeArrow}`} aria-hidden />
            ) : null}
            {record.valorNovo ? <span className={styles.newValue}>{record.valorNovo}</span> : null}
          </span>
        ) : null}
      </div>
    );
  }

  function renderModulo(record: AuditRecord) {
    if (!record.modulo) return null;

    return <span className={styles.tag}>{formatAuditModule(record.modulo)}</span>;
  }

  function renderResponsavel(record: AuditRecord) {
    return <ResponsavelTooltip record={record} />;
  }

  function renderActions(record: AuditRecord) {
    return (
      <div className={styles.rowActions}>
        <Button
          type="button"
          className={styles.rowActionButton}
          icon="pi pi-search"
          aria-label={`Detalhar registro de ${record.acao || "auditoria"}`}
          aria-haspopup="dialog"
          tooltip={DETAIL_ACTION_TOOLTIP}
          tooltipOptions={ACTION_TOOLTIP_OPTIONS}
          onClick={() => handleDetail(record)}
          data-testid={`audit-detail-${record.id}`}
          text
        />
      </div>
    );
  }

  if (status === "forbidden") {
    return (
      <section className={styles.container} data-testid="audit-screen">
        <StateMessage
          icon="pi pi-lock"
          title="Acesso não autorizado"
          description={errorMessage ?? undefined}
          testId="audit-forbidden"
        />
      </section>
    );
  }

  return (
    <section className={styles.container} data-testid="audit-screen">
      <Toast ref={toast} position="top-right" />

      <div className={styles.filters} data-testid="audit-filters">
        {/* Só o ano: a consulta recorta o período por ano, sem dia nem hora. */}
        <div className={styles.yearField}>
          <i className={`pi pi-calendar ${styles.yearIcon}`} aria-hidden />
          {anoDate ? (
            <span className={styles.yearPrefix} aria-hidden>
              Ano:
            </span>
          ) : null}
          <Calendar
            inputId="audit-filter-ano"
            className={`${styles.yearCalendar} ${anoCalendarOpen ? styles.yearCalendarOpen : ""}`}
            inputClassName={styles.yearInput}
            panelClassName={styles.calendarPanel}
            value={anoDate}
            onChange={(event) =>
              updateFilters({
                ano: event.value instanceof Date ? event.value.getFullYear() : null,
              })
            }
            onShow={() => setAnoCalendarOpen(true)}
            onHide={() => setAnoCalendarOpen(false)}
            view="year"
            dateFormat="yy"
            placeholder="Ano"
            ariaLabel="Ano"
            locale="pt-br"
            readOnlyInput
            showIcon
            icon="pi pi-chevron-down"
            data-testid="audit-filter-ano"
          />
        </div>

        {/* Alteração e Módulo têm valores fixos; a busca no painel encurta o caminho. */}
        <Dropdown
          inputId="audit-filter-alteracao"
          className={styles.filterDropdown}
          panelClassName={styles.filterPanel}
          value={filters.alteracao}
          options={AUDIT_ACTION_OPTIONS}
          onChange={(event) =>
            updateFilters({ alteracao: (event.value as AuditAction | "" | null) || "" })
          }
          optionLabel="label"
          optionValue="value"
          placeholder={ANY_ALTERACAO.label}
          filter
          filterBy="label"
          filterDelay={0}
          filterPlaceholder={ALTERACAO_FILTER_PLACEHOLDER}
          emptyFilterMessage={ALTERACAO_EMPTY_FILTER_MESSAGE}
          aria-label="Alteração"
          data-testid="audit-filter-alteracao"
        />

        <Dropdown
          inputId="audit-filter-modulo"
          className={styles.filterDropdown}
          panelClassName={styles.filterPanel}
          value={filters.modulo}
          options={AUDIT_MODULE_OPTIONS}
          onChange={(event) =>
            updateFilters({ modulo: (event.value as AuditModule | "" | null) || "" })
          }
          optionLabel="label"
          optionValue="value"
          placeholder={ANY_MODULO.label}
          filter
          filterBy="label"
          filterDelay={0}
          filterPlaceholder={MODULO_FILTER_PLACEHOLDER}
          emptyFilterMessage={MODULO_EMPTY_FILTER_MESSAGE}
          aria-label="Módulo"
          data-testid="audit-filter-modulo"
        />

        {/* Responsável vem do cadastro de usuários; a consulta recebe o id escolhido. */}
        <Dropdown
          inputId="audit-filter-responsavel"
          className={styles.filterDropdown}
          panelClassName={styles.filterPanel}
          value={filters.responsavelId}
          options={responsavelOptions.options}
          onChange={(event) =>
            updateFilters({ responsavelId: (event.value as string | null) ?? "" })
          }
          optionLabel="label"
          optionValue="value"
          placeholder={ANY_RESPONSAVEL.label}
          filter
          filterBy="label"
          filterDelay={0}
          filterPlaceholder={RESPONSAVEL_FILTER_PLACEHOLDER}
          emptyFilterMessage={RESPONSAVEL_EMPTY_FILTER_MESSAGE}
          aria-label="Responsável"
          disabled={responsavelOptions.isLoading}
          data-testid="audit-filter-responsavel"
        />

        <Button
          type="button"
          className={styles.clearButton}
          label="Limpar Filtros"
          onClick={handleClear}
          data-testid="audit-filter-clear"
        />

        <Button
          type="button"
          className={styles.configButton}
          icon="pi pi-ellipsis-v"
          aria-label="Opções da tabela"
          aria-haspopup
          onClick={(event) => configPanel.current?.toggle(event)}
          data-testid="audit-table-config"
          severity="secondary"
          outlined
        />

        <OverlayPanel ref={configPanel} className={styles.configPanel} dismissable>
          <div className={styles.configBody} data-testid="audit-table-config-panel">
            <button
              type="button"
              className={styles.configAction}
              onClick={handleExportView}
              data-testid="audit-export-view"
            >
              <i className="pi pi-download" aria-hidden />
              Exportar Visualização
            </button>

            <button
              type="button"
              className={styles.configAction}
              aria-haspopup="dialog"
              onClick={handleOpenSettings}
              data-testid="audit-table-settings-toggle"
            >
              <i className="pi pi-cog" aria-hidden />
              Configurações da Tabela
            </button>
          </div>
        </OverlayPanel>
      </div>

      {exportView.isOpen ? (
        <ExportTableDialog
          listName="registros de auditoria"
          isExporting={exportView.isExporting}
          onExport={exportView.run}
          onCancel={exportView.close}
        />
      ) : null}

      {isSettingsOpen ? (
        <TableSettingsDialog
          settings={settings}
          onApply={handleApplySettings}
          onCancel={() => setIsSettingsOpen(false)}
        />
      ) : null}

      {detailId !== null ? (
        <AuditDetailDialog
          recordId={detailId}
          onClose={() => setDetailId(null)}
          onReauthenticate={handleReauthenticate}
        />
      ) : null}

      {status === "error" ? (
        <StateMessage
          icon="pi pi-exclamation-triangle"
          title="Não foi possível carregar os registros de auditoria."
          description={errorMessage ?? undefined}
          testId="audit-error"
        >
          <Button
            type="button"
            className={styles.retryButton}
            label="Tentar novamente"
            icon="pi pi-refresh"
            onClick={reload}
            data-testid="audit-retry"
          />
        </StateMessage>
      ) : (
        <div className={styles.tableWrapper}>
          {isLoading ? (
            <span className={styles.srOnly} role="status" data-testid="audit-loading">
              Carregando registros de auditoria...
            </span>
          ) : null}

          {isEmpty ? (
            <EmptyState
              kind={emptyKind}
              content={emptyContent}
              onClear={emptyKind === "filter" ? handleClear : undefined}
              testId="audit-empty"
            />
          ) : (
            <DataTable
              // As células são memoizadas pela posição na linha: sem remontar, esconder
              // uma coluna deixa as últimas células com o conteúdo do arranjo anterior.
              key={settings.columns.join("|")}
              className={styles.table}
              value={records}
              dataKey="id"
              loading={isLoading}
              size={settings.density === "compacto" ? "small" : "normal"}
              tableStyle={{ minWidth: "63rem" }}
              emptyMessage={emptyContent.title}
              lazy
              totalRecords={total}
              paginator={total > 0}
              paginatorClassName={styles.paginator}
              paginatorTemplate={PAGINATOR_TEMPLATE}
              currentPageReportTemplate={CURRENT_PAGE_REPORT}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              first={first}
              rows={rows}
              onPage={handlePage}
              // Sem ordenação escolhida a tabela não destaca nenhum cabeçalho e o
              // próximo clique começa o ciclo pelo crescente.
              sortField={sort?.field}
              sortOrder={sort ? (sort.direction === "asc" ? 1 : -1) : null}
              onSort={handleSort}
              sortIcon={renderSortIcon}
              // Habilita o terceiro estado do ciclo: sem ordenação (RN02).
              removableSort
              aria-label="Registros de auditoria"
              data-testid="audit-table"
            >
              {shows("dataHora") ? (
                <Column
                  field="dataHora"
                  header="Data/Hora"
                  style={COLUMN_WIDTHS.dataHora}
                  body={renderDataHora}
                  sortable
                />
              ) : null}
              <Column
                field="acao"
                header="Ação"
                style={COLUMN_WIDTHS.acao}
                body={renderAcao}
                sortable
              />
              {shows("justificativa") ? (
                <Column
                  field="justificativa"
                  header="Justificativa"
                  style={COLUMN_WIDTHS.justificativa}
                  sortable
                />
              ) : null}
              {shows("modulo") ? (
                <Column
                  field="modulo"
                  header="Módulo"
                  style={COLUMN_WIDTHS.modulo}
                  body={renderModulo}
                  sortable
                />
              ) : null}
              {shows("responsavel") ? (
                <Column
                  field="responsavel"
                  header="Responsável"
                  style={COLUMN_WIDTHS.responsavel}
                  body={renderResponsavel}
                  sortable
                />
              ) : null}
              <Column
                // Coluna de ações sem rótulo: a lupa já identifica o comando (padrão de Perfis).
                header=""
                headerClassName={styles.actionsHeader}
                bodyClassName={styles.actionsCell}
                style={COLUMN_WIDTHS.acoes}
                body={renderActions}
              />
            </DataTable>
          )}
        </div>
      )}
    </section>
  );
}
